import { call, put, select, takeLatest } from 'redux-saga/effects'

import {
  AgentSourcedRunScope,
  IAgentSourcedCreator,
  IAgentSourcedCreatorFilters,
  IAgentSourcedCreatorMeta,
  ISourcingRun,
} from '@components/CreatorSearch/types'
import { getEffectiveSelectedRun } from '@components/CreatorSearch/utils/effectiveSelectedRun'
import {
  buildScopeFilterParams,
  buildSortParams,
} from '@components/CreatorSearch/utils/filterParams'
import { IHateoasLink } from '@store'
import {
  fetchAgentSourcedCreators,
  fetchAgentSourcedCreatorsFailure,
  fetchAgentSourcedCreatorsSuccess,
} from '@store/agentSourcedCreators/actions'
import { selectSelectedRunScope, selectSourcingRuns } from '@store/agentSourcedCreators/selectors'
import { selectAuthToken } from '@store/auth/selectors'
import { selectCampaignLink } from '@store/campaigns'
import { selectAgentSourcedCreatorsLink } from '@store/me'
import { networkRequest } from '@utils/networkRequest'

const VIBE_CHECK_LIST_REL = 'agent_sourced_creators'
const INTERESTED_REL = 'interested_candidates'
const SHORTLISTED_REL = 'shortlisted_candidates'

interface IFetchResponse {
  items: IAgentSourcedCreator[]
  links: IHateoasLink[]
  metadata: IAgentSourcedCreatorMeta | null
}

// BRAND-9550: the membership-scoping params come from the shared builder so
// this query and the bulk-reject scope body can never drift apart (a drift is
// a silent wrong-set rejection). Sort params are appended after them, keeping
// the historical query-string ordering byte-for-byte.
const appendFilterParams = (url: URL, filters: IAgentSourcedCreatorFilters | undefined): void => {
  if (!filters) return
  Object.entries({
    ...buildScopeFilterParams(filters),
    ...buildSortParams(filters),
  }).forEach(([key, value]) => url.searchParams.set(key, value))
}

// BRAND-9306 run-aware review views: appends `?sourcing_run=<ordinal>|all`.
// `null` is only still possible on a genuinely cold fetch (no sourcing runs
// known yet — see the effective-scope resolution in the saga below) and is
// omitted entirely; the BE resolves the omission to its own default run.
const appendRunScopeParam = (url: URL, scope: AgentSourcedRunScope): void => {
  if (scope === null) return
  url.searchParams.set('sourcing_run', String(scope))
}

export function* fetchCreatorsSaga(action: ReturnType<typeof fetchAgentSourcedCreators>) {
  try {
    const { campaignId, filters, urlOverride, append, listing } = action.payload
    const token: string = yield select(selectAuthToken)
    const selectedRunScope: AgentSourcedRunScope = yield select(selectSelectedRunScope)
    const sourcingRuns: ISourcingRun[] = yield select(selectSourcingRuns)
    // BRAND-9515 fix (stale list after starting a new run — Archer QA
    // repro): resolve the request's scope to the SAME effective run
    // getEffectiveSelectedRun resolves for RunTabs/the empty-state gating,
    // and send it EXPLICITLY whenever any runs are known. Trusting the BE's
    // own omitted-param default here previously let it disagree with that
    // contract (starting a new run reset the scope to null — ca9000558 —
    // without ever sending an explicit ordinal, so the list could resolve a
    // different run than the tab strip/record bar already showed as
    // selected and importing, until a manual tab round-trip). An explicit
    // ordinal that matches no known run (stale/out-of-range) falls back to
    // the same effective default, mirroring getEffectiveSelectedRun's own
    // contract. Only a genuinely cold fetch (sourcingRuns still empty — no
    // campaign snapshot or list metadata has landed yet) still omits the
    // param; the list refetches once runs land and the selection resolves.
    const runScope: AgentSourcedRunScope =
      selectedRunScope === 'all'
        ? 'all'
        : getEffectiveSelectedRun(sourcingRuns, selectedRunScope)?.ordinal ?? selectedRunScope

    let href: string
    let method: string

    if (urlOverride) {
      // HATEOAS prev/next: use the BE-provided URL verbatim (already includes
      // any cursor/page params, filters AND sourcing_run from the original
      // request) — mirrors why appendFilterParams is skipped here too.
      href = urlOverride.href
      method = urlOverride.method
    } else if (listing === 'interested' || listing === 'shortlisted') {
      // Customer shortlist tabs: follow the brief-scoped HATEOAS link surfaced on
      // the campaign resource. The BE omits these links until
      // brief.vibe_check_completed? is true (brief_links_view.rb), so absence
      // signals "shortlist not yet available". Callers are expected to gate
      // dispatch on isCampaignFetched so the campaign is in store by the time
      // we reach here.
      const rel = listing === 'shortlisted' ? SHORTLISTED_REL : INTERESTED_REL
      const link: IHateoasLink | undefined = yield select(selectCampaignLink, campaignId, rel)
      if (!link) {
        yield put(
          fetchAgentSourcedCreatorsFailure(
            `HATEOAS link '${rel}' not found on campaign ${campaignId}; vibe check may not be complete yet.`,
          ),
        )
        return
      }
      const url = new URL(link.href)
      appendFilterParams(url, filters)
      appendRunScopeParam(url, runScope)
      href = url.toString()
      method = link.method
    } else {
      // Default (vibe-check): /me-level template, substitute brief and strip trailing /{agent_sourced_creator_id}
      const link: IHateoasLink | undefined = yield select(selectAgentSourcedCreatorsLink)
      if (!link) {
        throw new Error(`HATEOAS link '${VIBE_CHECK_LIST_REL}' not found on /me`)
      }
      const collectionHref = link.href
        .replace('{brief_id}', String(campaignId))
        .replace('/{agent_sourced_creator_id}', '')
      const url = new URL(collectionHref)
      appendFilterParams(url, filters)
      appendRunScopeParam(url, runScope)
      href = url.toString()
      method = link.method
    }

    const response: IFetchResponse = yield call(networkRequest, method, href, undefined, token)

    yield put(
      fetchAgentSourcedCreatorsSuccess({
        campaignId,
        creators: response.items,
        collectionLinks: response.links,
        metadata: response.metadata ?? null,
        append,
        listing: listing ?? 'vibe-check',
      }),
    )
  } catch (e: any) {
    yield put(fetchAgentSourcedCreatorsFailure(e?.message ?? 'Failed to load creators'))
  }
}

export default function* watchFetchAgentSourcedCreators() {
  yield takeLatest(fetchAgentSourcedCreators.type, fetchCreatorsSaga)
}
